import React, { useEffect } from 'react'

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'
import schema from './schema'

const DialogNewCommittee = ({ open, setOpen, onEvent }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const {
    response: committeeCategoriesResponse,
    error: committeeCategoriesError,
  } = useFetch(
    service.dponet.committeeCategories.index,
    { perPage: 99999 },
    [open],
    open,
  )

  const committeeCategories = (
    committeeCategoriesResponse?.data?.committeeCategories || []
  ).filter(
    (category) => category.name !== constants.committees.RECOMMENDED_COMMITTEE,
  )

  useEffect(() => {
    if (!committeeCategoriesError) {
      return
    }

    snackbar.open({
      message: helpers.formatters.errorMessage(committeeCategoriesError, false),
      variant: 'error',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committeeCategoriesError])

  const {
    handleSubmit,
    control,
    errors,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm({
    validationSchema: schema(),
    defaultValues: {
      otherCommitteesSelect: '',
      anotherCommitteeCategory: '',
    },
  })

  useEffect(() => {
    if (!open) {
      return
    }

    reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const findExistingCategory = (name) =>
    committeeCategories.find(
      (category) => category.name.toLowerCase() === name.toLowerCase(),
    )

  const otherCommitteesSelect = watch('otherCommitteesSelect')
  const isAnotherCommitteeCategory =
    otherCommitteesSelect ===
    constants.committees.ANOTHER_COMMITTEE_CATEGORY_VALUE

  const resolveCommitteeName = async (data) => {
    const recommendedCommitteeVerifier = {
      [constants.committees.FORM_RECOMMENDED_COMMITTEE]:
        constants.committees.RECOMMENDED_COMMITTEE,
    }

    const recommendedCommitteeName =
      recommendedCommitteeVerifier[data?.formType]

    if (recommendedCommitteeName) {
      return recommendedCommitteeName
    }

    if (
      data?.otherCommitteesSelect ===
      constants.committees.ANOTHER_COMMITTEE_CATEGORY_VALUE
    ) {
      const committeeName = data?.anotherCommitteeCategory

      if (!findExistingCategory(committeeName)) {
        await service.dponet.committeeCategories.create({
          name: committeeName,
        })
      }

      return committeeName
    }

    const selectedCategory = committeeCategories.find(
      (category) => `${category.id}` === data?.otherCommitteesSelect,
    )

    return selectedCategory?.name
  }

  const onSubmit = async (data) => {
    try {
      const committeeName = await resolveCommitteeName(data)

      await service.dponet.committees.create({
        committee: { name: committeeName },
      })

      snackbar.open({
        message: 'Comitê criado com sucesso',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error, false) ||
          error?.error?.message,
        variant: 'error',
      })
    } finally {
      setOpen(false)
      onEvent()
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box>
            <Typography variant="h5">Novo comitê</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Controller
                  control={control}
                  name="formType"
                  defaultValue=""
                  as={
                    <RadioGroup>
                      <FormControlLabel
                        value={constants.committees.FORM_RECOMMENDED_COMMITTEE}
                        control={<Radio />}
                        className={classes.optionBox}
                        label={
                          <>
                            <Typography
                              variant="h5"
                              className={classes.optionTitle}
                              color={errors.formType ? 'error' : 'textPrimary'}
                            >
                              Criar Comitê de Proteção e Privacidade de Dados
                              <Chip
                                size="small"
                                className={classes.recommended}
                                label="RECOMENDADO"
                              />
                            </Typography>
                            <Typography
                              variant="body1"
                              color={errors.formType ? 'error' : 'textPrimary'}
                            >
                              Comece criando o comitê que define diretrizes e
                              coordena ações de conformidade com a LGPD.
                            </Typography>
                          </>
                        }
                      />
                      <FormControlLabel
                        value={constants.committees.FORM_OTHER_COMMITTEE}
                        control={<Radio />}
                        className={classes.optionBox}
                        label={
                          <>
                            <Typography
                              variant="h5"
                              className={classes.optionTitle}
                              color={errors.formType ? 'error' : 'textPrimary'}
                            >
                              Outros comitês
                            </Typography>
                            <Typography
                              variant="body1"
                              color={errors.formType ? 'error' : 'textPrimary'}
                            >
                              Crie mais comitês especializados para
                              acompanhamento contínuo (opcional).
                            </Typography>
                          </>
                        }
                      />
                    </RadioGroup>
                  }
                  mode="onChange"
                />
              </FormControl>
            </Grid>
            {watch('formType') ===
              constants.committees.FORM_OTHER_COMMITTEE && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="otherCommitteesSelect"
                  as={
                    <TextField
                      label="Selecione o comitê especializado"
                      select
                      color="primary"
                      variant="outlined"
                      error={!!errors.otherCommitteesSelect}
                      helperText={errors?.otherCommitteesSelect?.message}
                      fullWidth
                    >
                      {committeeCategories?.map((category) => (
                        <MenuItem key={category.id} value={`${category.id}`}>
                          <Tooltip title={category.name}>
                            <Typography
                              noWrap
                              className={classes.truncatedText}
                            >
                              {category.name}
                            </Typography>
                          </Tooltip>
                        </MenuItem>
                      ))}
                      <MenuItem
                        key={
                          constants.committees.ANOTHER_COMMITTEE_CATEGORY_VALUE
                        }
                        value={
                          constants.committees.ANOTHER_COMMITTEE_CATEGORY_VALUE
                        }
                      >
                        <Typography>Outros</Typography>
                      </MenuItem>
                    </TextField>
                  }
                  mode="onBlur"
                />
                {isAnotherCommitteeCategory && (
                  <Box mt={2}>
                    <Controller
                      control={control}
                      name="anotherCommitteeCategory"
                      as={
                        <TextField
                          label="Nome do comitê"
                          placeholder="Digite o nome do comitê"
                          color="primary"
                          variant="outlined"
                          error={!!errors.anotherCommitteeCategory}
                          helperText={errors?.anotherCommitteeCategory?.message}
                          fullWidth
                        />
                      }
                    />
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions p={2} className={classes.buttonBox}>
          <Box pl={2} pr={1}>
            <Button type="button" variant="text" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
          </Box>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={isSubmitting}
          >
            Criar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default DialogNewCommittee
